import clsx from "clsx";
import type { FC, HTMLAttributes, MouseEvent } from "react";
import { useEffect, useRef } from "react";

import XMark from "@dndbeyond/fontawesome-cache/svgs/solid/xmark.svg";
import { Button } from "@dndbeyond/ttui/lexicon/components/Button";

import styles from "./styles.module.css";

export interface FilterDrawerProps extends HTMLAttributes<HTMLDialogElement> {
  open: boolean;
  onClose: () => void;
  onReset: () => void;
  hasActiveFilters: boolean;
}

const TITLE_ID = "premade-filter-drawer-title";

export const FilterDrawer: FC<FilterDrawerProps> = ({
  open,
  onClose,
  onReset,
  hasActiveFilters,
  className,
  children,
  ...props
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  // Esc fires "cancel" - keep React state as the source of truth
  useEffect(() => {
    const dialog = dialogRef.current;
    const handleCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    dialog?.addEventListener("cancel", handleCancel);
    return () => dialog?.removeEventListener("cancel", handleCancel);
  }, [onClose]);

  // Close when the dimmed backdrop is clicked. Backdrop clicks are reported on
  // the <dialog> element itself, while clicks inside the drawer panel target its children.
  const handleClick = (e: MouseEvent<HTMLDialogElement>) => {
    if (e.target === dialogRef.current) onClose();
  };

  return (
    <dialog
      ref={dialogRef}
      className={clsx([styles.drawer, className])}
      aria-labelledby={TITLE_ID}
      onClick={handleClick}
      {...props}
    >
      <div className={styles.panel}>
        <header className={styles.header}>
          <h2 id={TITLE_ID} className={styles.title}>
            Filters
          </h2>
          <Button
            color="icon"
            size="small"
            onClick={onClose}
            aria-label="Close filters"
          >
            <XMark />
          </Button>
        </header>
        <div className={styles.body}>{children}</div>
        <footer className={styles.footer}>
          <Button
            className={styles.footerButton}
            color="secondary"
            size="small"
            onClick={onReset}
            disabled={!hasActiveFilters}
          >
            Reset
          </Button>
          <Button
            className={styles.footerButton}
            color="primary"
            size="small"
            onClick={onClose}
          >
            Apply
          </Button>
        </footer>
      </div>
    </dialog>
  );
};
